import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import LegalComplianceStamp from '../../components/legal/LegalComplianceStamp';

/**
 * Version enrichie basée sur le dossier juridique PADAID.
 * Les zones [ ] et les mentions « en cours de validation juridique » doivent être
 * finalisées avec un juriste (notamment la section Mineurs et la conservation de l'IP).
 */
const PolitiqueConfidentialite = () => {
  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-primary mb-6" data-testid="legal-back-link">
          <ArrowLeft className="mr-2 h-4 w-4" /> Retour à l'accueil
        </Link>
        <h1 className="text-3xl font-bold mb-2">Politique de confidentialité</h1>
        <p className="text-sm text-muted-foreground mb-8">Dernière mise à jour : juillet 2026</p>

        <div className="prose prose-sm max-w-none space-y-6 text-muted-foreground">
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-2">Responsable du traitement</h2>
            <p>PADAID (Pont Alternatif D'AIDE), association régie par la loi du 1er juillet 1901 (RNA W062020401), est responsable du traitement des données à caractère personnel collectées via ce site. Pour toute question relative à vos données : padaid.asso@gmail.com.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-2">Données que nous collectons</h2>
            <p>Selon votre utilisation du site, nous pouvons collecter :</p>
            <ul className="list-disc pl-6">
              <li>Données d'identification : nom, prénom, adresse e-mail, numéro de téléphone (à l'inscription ou l'adhésion) ;</li>
              <li>Mot de passe, stocké sous forme chiffrée — jamais en clair ;</li>
              <li>Données de connexion : adresse IP, date et heure, type de navigateur/appareil, et résultat (réussite ou échec) de chaque connexion, à des fins de sécurité ;</li>
              <li>Données de navigation : pages consultées, page d'origine, fuseau horaire et adresse IP, à des fins de mesure d'audience et de sécurité ;</li>
              <li>Contenus de la messagerie interne : messages texte et messages vocaux que vous échangez ;</li>
              <li>Signalements et blocages effectués dans la messagerie, à des fins de modération ;</li>
              <li>Données de paiement (dons, boutique), traitées par notre prestataire de paiement et jamais stockées en clair sur nos serveurs ;</li>
              <li>Contenus que vous choisissez de partager (messages de contact, motivations d'adhésion, publications).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-2">Finalités et bases légales</h2>
            <ul className="list-disc pl-6">
              <li>Gestion de votre compte et de votre adhésion — exécution du contrat d'adhésion ;</li>
              <li>Traitement des dons et achats — exécution du contrat et obligations comptables légales ;</li>
              <li>Communication associative — consentement ou intérêt légitime ;</li>
              <li>Sécurité du site, journalisation des connexions et des adresses IP, prévention de la fraude et des accès non autorisés — intérêt légitime de l'association ;</li>
              <li>Géolocalisation ponctuelle d'une adresse IP en cas d'incident de sécurité — intérêt légitime.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-2">Journalisation de sécurité et traçabilité</h2>
            <p>À des fins de sécurité, l'association conserve un journal des connexions (adresse IP, date, heure, appareil) et des visites. Les personnes habilitées de l'association peuvent, en cas d'incident ou de signalement, consulter l'activité associée à un compte ou à une adresse IP. Ce traitement est strictement encadré et réservé aux responsables autorisés.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-2">Attention particulière : mineurs</h2>
            <p>PADAID intervient dans un contexte pouvant impliquer des personnes mineures. Conformément à l'article 45 de la loi n° 78-17 du 6 janvier 1978 (Informatique et Libertés) et à l'article 8 du RGPD, un mineur peut consentir seul au traitement de ses données personnelles à partir de <strong>15 ans</strong>. En deçà de 15 ans, le consentement est donné conjointement par le mineur et le ou les titulaires de l'autorité parentale.</p>
            <p>Pour tout compte de mineur de moins de 15 ans, PADAID recueille et vérifie le consentement du titulaire de l'autorité parentale, applique une minimisation renforcée des données, limite leur durée de conservation, ne les rend pas publiques, et facilite l'exercice des droits (accès, rectification, effacement) pour le compte du mineur. Les titulaires de l'autorité parentale peuvent à tout moment exercer ces droits en écrivant à padaid.asso@gmail.com.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-2">Destinataires et sous-traitants</h2>
            <p>Vos données peuvent être communiquées à des prestataires techniques agissant pour le compte de PADAID :</p>
            <ul className="list-disc pl-6">
              <li>Infomaniak Network SA (hébergement — Suisse) ;</li>
              <li>Stripe (traitement des paiements — États-Unis/Irlande) ;</li>
              <li>Google (connexion « Se connecter avec Google » — États-Unis) ;</li>
              <li>Service de géolocalisation d'adresse IP (à la demande, à des fins de sécurité — États-Unis) ;</li>
              <li>YouTube (lecture des vidéos intégrées à la galerie — États-Unis).</li>
            </ul>
            <p>PADAID ne vend ni ne loue vos données personnelles.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-2">Transferts hors Union européenne</h2>
            <p>Certains prestataires sont situés hors de l'Union européenne. Ces transferts sont encadrés par des garanties appropriées (décision d'adéquation pour la Suisse, clauses contractuelles types et cadres de protection des données pour les prestataires établis aux États-Unis).</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-2">Durées de conservation</h2>
            <p>Vos données sont conservées pour la durée nécessaire aux finalités poursuivies : le temps de votre adhésion (puis une période d'archivage limitée), une durée limitée pour les journaux de connexion et de visite, et les durées légales pour les pièces comptables liées aux dons. <em>[Durées précises par catégorie en cours de finalisation.]</em></p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-2">Vos droits</h2>
            <p>Conformément au RGPD, vous disposez des droits d'accès, de rectification, d'effacement, de limitation, de portabilité et d'opposition concernant vos données. Pour les exercer : padaid.asso@gmail.com. Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-2">Sécurité</h2>
            <p>PADAID met en œuvre des mesures techniques et organisationnelles : mots de passe chiffrés, connexions sécurisées (HTTPS), cookie d'authentification protégé, accès aux données strictement réservé aux personnes habilitées, double authentification pour les comptes d'administration, protection contre les tentatives répétées de connexion, et journalisation des accès.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-2">Cookies</h2>
            <p>Le site utilise des cookies strictement nécessaires à son fonctionnement et à la sécurité (cookie d'authentification, cookie de session pour la connexion via Google), ainsi qu'une mesure d'audience interne. Les vidéos de la galerie, lorsqu'elles sont lues, peuvent déposer des cookies tiers (YouTube). Vous pouvez gérer vos préférences via le bandeau de consentement présenté lors de votre visite.</p>
          </section>
          <LegalComplianceStamp />
        </div>
      </div>
    </div>
  );
};

export default PolitiqueConfidentialite;
